import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Home, Plus, Edit, Archive, ArchiveRestore, Eye, MapPin, Users, Bed, Trash2 } from 'lucide-react';
import { EmptyState } from '@/components/common/EmptyState';
import { IconButton } from '@/components/common/IconButton';
import { Skeleton } from '@/components/ui/skeleton';
import HouseImage from '@/components/houses/HouseImage';

function SiteInfoFields({ formData, setFormData, idPrefix }) {
  const set = (k, v) => setFormData((f) => ({ ...f, [k]: v }));
  const contacts = formData.important_contacts || [];
  const setContact = (i, k, v) => set('important_contacts', contacts.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const addContact = () => set('important_contacts', [...contacts, { role: '', name: '', phone: '' }]);
  const removeContact = (i) => set('important_contacts', contacts.filter((_, idx) => idx !== i));
  const field = (key, label, placeholder) => (
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}_${key}`}>{label}</Label>
      <Input id={`${idPrefix}_${key}`} value={formData[key] || ''} onChange={(e) => set(key, e.target.value)} placeholder={placeholder} data-testid={`house-${idPrefix}-${key.replace(/_/g, '-')}-input`} />
    </div>
  );
  return (
    <>
      <div className="pt-2 text-sm font-semibold text-muted-foreground">Access &amp; keys</div>
      {field('access_front_door_code', 'Front door code', 'e.g. 1234')}
      {field('access_lockbox', 'Lockbox', 'e.g. Garage wall keybox — code 2298')}
      {field('access_spare_key', 'Spare key', 'e.g. Held by neighbour at No. 10 (Maria)')}
      {field('access_alarm_code', 'Alarm code', 'e.g. 4321')}
      <div className="pt-2 text-sm font-semibold text-muted-foreground">Wifi &amp; internet</div>
      {field('wifi_network', 'Network name', 'e.g. MarionAve_5G')}
      {field('wifi_password', 'Wifi password', 'Wifi password')}
      <div className="pt-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-muted-foreground">Important contacts</span>
        <Button type="button" variant="outline" size="sm" onClick={addContact} data-testid={`house-${idPrefix}-add-contact-btn`}>Add contact</Button>
      </div>
      {contacts.map((c, i) => (
        <div key={i} className="grid grid-cols-3 gap-2 items-end border rounded-md p-2">
          <div className="space-y-1"><Label className="text-xs">Role</Label><Input value={c.role || ''} onChange={(e) => setContact(i, 'role', e.target.value)} placeholder="Property Manager" data-testid={`house-${idPrefix}-contact-role-${i}`} /></div>
          <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={c.name || ''} onChange={(e) => setContact(i, 'name', e.target.value)} placeholder="Liza Huang" data-testid={`house-${idPrefix}-contact-name-${i}`} /></div>
          <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={c.phone || ''} onChange={(e) => setContact(i, 'phone', e.target.value)} placeholder="02 9877 1234" data-testid={`house-${idPrefix}-contact-phone-${i}`} /></div>
          <div className="col-span-3 flex justify-end"><Button type="button" variant="ghost" size="sm" onClick={() => removeContact(i)} data-testid={`house-${idPrefix}-contact-remove-${i}`}>Remove</Button></div>
        </div>
      ))}
    </>
  );
}

export default function HousesListPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);   // blocks repeat Create/Save clicks while the request is in flight
  const apiMessage = (error, fallback) => error.response?.data?.error?.message || error.response?.data?.detail?.message || error.response?.data?.message || fallback;
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [formData, setFormData] = useState({
    house_no: '',
    name: '',
    address: '',
    capacity: '',
    access_front_door_code: '',
    access_lockbox: '',
    access_spare_key: '',
    access_alarm_code: '',
    wifi_network: '',
    wifi_password: '',
    important_contacts: []
  });

  useEffect(() => {
    fetchHouses();
  }, []);

  const fetchHouses = async () => {
    try {
      const response = await api.get('/houses');
      setHouses(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load houses');
    } finally {
      setLoading(false);
    }
  };

  const uploadHousePhoto = async (houseId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    await api.post(`/houses/${houseId}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const res = await api.post('/houses', {
        ...formData,
        capacity: parseInt(formData.capacity) || 0
      });
      const newId = res.data?.data?.id;
      if (imageFile && newId) {
        try { await uploadHousePhoto(newId, imageFile); }
        catch { toast.error('House created, but the image upload failed.'); }
      }
      toast.success('House created successfully');
      setShowCreateDialog(false);
      setImageFile(null);
      setFormData({ house_no: '', name: '', address: '', capacity: '', access_front_door_code: '', access_lockbox: '', access_spare_key: '', access_alarm_code: '', wifi_network: '', wifi_password: '', important_contacts: [] });
      fetchHouses();
    } catch (error) {
      toast.error(apiMessage(error, 'Failed to create house'));
    } finally { setSaving(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api.put(`/houses/${selectedHouse.id}`, {
        ...formData,
        capacity: parseInt(formData.capacity) || 0
      });
      if (imageFile) {
        try { await uploadHousePhoto(selectedHouse.id, imageFile); }
        catch { toast.error('House saved, but the image upload failed.'); }
      }
      toast.success('House updated successfully');
      setShowEditDialog(false);
      setSelectedHouse(null);
      setImageFile(null);
      fetchHouses();
    } catch (error) {
      toast.error(apiMessage(error, 'Failed to update house'));
    } finally { setSaving(false); }
  };

  const handleArchive = async (houseId) => {
    if (!confirm('Are you sure you want to archive this house?')) return;
    try {
      await api.post(`/houses/${houseId}/archive`);
      toast.success('House archived successfully');
      fetchHouses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to archive house');
    }
  };

  const handleUnarchive = async (houseId) => {
    if (!confirm('Restore this house to active status?')) return;
    try {
      await api.post(`/houses/${houseId}/unarchive`);
      toast.success('House unarchived successfully');
      fetchHouses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to unarchive house');
    }
  };

  // BS3-028: permanently delete every archived house (and its rooms) — cannot be undone
  const [emptying, setEmptying] = useState(false);
  const handleEmptyArchived = async (count) => {
    if (!confirm(`Permanently delete ${count} archived ${count === 1 ? 'house' : 'houses'} and their rooms? This cannot be undone.`)) return;
    setEmptying(true);
    try {
      const r = await api.post('/houses/archived/empty');
      const n = r.data?.data?.deleted_count ?? 0;
      toast.success(`${n} archived ${n === 1 ? 'house' : 'houses'} deleted`);
      fetchHouses();
    } catch (error) {
      toast.error(apiMessage(error, 'Failed to delete archived houses'));
    } finally { setEmptying(false); }
  };

  // BS3-028: permanently delete ONE archived house (and its rooms) — cannot be undone
  const handleDeleteArchived = async (house) => {
    if (!confirm(`Permanently delete "${house.name}" and its rooms? This cannot be undone.`)) return;
    try {
      await api.delete(`/houses/${house.id}`);
      toast.success(`"${house.name}" deleted`);
      fetchHouses();
    } catch (error) {
      toast.error(apiMessage(error, 'Failed to delete house'));
    }
  };

  const openEditDialog = (house) => {
    setSelectedHouse(house);
    setImageFile(null);
    setFormData({
      house_no: house.house_no || '',
      name: house.name || '',
      address: house.address || '',
      capacity: house.capacity?.toString() || '',
      access_front_door_code: house.access_front_door_code || '',
      access_lockbox: house.access_lockbox || '',
      access_spare_key: house.access_spare_key || '',
      access_alarm_code: house.access_alarm_code || '',
      wifi_network: house.wifi_network || '',
      wifi_password: house.wifi_password || '',
      important_contacts: house.important_contacts || []
    });
    setShowEditDialog(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      ACTIVE: 'default',
      INACTIVE: 'secondary',
      ARCHIVED: 'destructive'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-8 max-w-7xl mx-auto space-y-6" data-testid="houses-list-loading">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-7 w-44" />
            <Skeleton className="h-3 w-64 mt-2" />
          </div>
          <Skeleton className="h-9 w-28" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44 rounded-lg" />)}
        </div>
      </div>
    );
  }

  // Polish-pass KPI band — additive testids only, no backend change.
  const kpiTotal = houses.length;
  const kpiActive = houses.filter(h => h.status === 'ACTIVE').length;
  const kpiCapacity = houses.reduce((s, h) => s + (h.capacity || 0), 0);
  const kpiRooms = houses.reduce((s, h) => s + (h.room_count || 0), 0);

  const archivedHouses = houses.filter(h => h.status === 'ARCHIVED');
  const activeHouses = houses.filter(h => h.status !== 'ARCHIVED');

  const renderHouseCard = (house) => (
          <Card key={house.id} className={`hover:shadow-lg transition-shadow ${house.status === 'ARCHIVED' ? 'opacity-80 bg-slate-50/60' : ''}`} data-testid={`house-card-${house.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg">{house.name}</CardTitle>
                  <CardDescription className="text-xs">{house.house_no}</CardDescription>
                </div>
                {getStatusBadge(house.status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-2">{house.address || 'No address'}</span>
                </div>
                <div className="flex items-center gap-4 text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>{house.capacity || 0} capacity</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Bed className="h-4 w-4" />
                    <span>{house.room_count || 0} rooms</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => navigate(`/houses/${house.id}`)}
                  data-testid={`view-house-${house.id}`}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => navigate(`/accommodation/houses/${house.id}/rooms`)}
                  data-testid={`rooms-house-${house.id}`}
                >
                  <Bed className="h-4 w-4 mr-1" />
                  Rooms
                </Button>
                {isAdmin && (
                  <>
                    <IconButton
                      label="Edit house"
                      onClick={() => openEditDialog(house)}
                      testid={`edit-house-${house.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </IconButton>
                    {house.status === 'ACTIVE' && (
                      <IconButton
                        label="Archive house"
                        onClick={() => handleArchive(house.id)}
                        testid={`archive-house-${house.id}`}
                      >
                        <Archive className="h-4 w-4" />
                      </IconButton>
                    )}
                    {house.status === 'ARCHIVED' && (
                      <IconButton
                        label="Unarchive house"
                        onClick={() => handleUnarchive(house.id)}
                        testid={`unarchive-house-${house.id}`}
                      >
                        <ArchiveRestore className="h-4 w-4" />
                      </IconButton>
                    )}
                    {house.status === 'ARCHIVED' && (
                      <IconButton
                        label="Delete house permanently"
                        onClick={() => handleDeleteArchived(house)}
                        testid={`delete-house-${house.id}`}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </IconButton>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
  );

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6" data-testid="houses-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Accommodation</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? 'Manage SIL houses and properties' : 'View SIL houses and properties'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-house-button">
            <Plus className="h-4 w-4 mr-2" />
            Add House
          </Button>
        )}
      </div>

      {/* KPI band */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="houses-summary">
        <div className="rounded-lg border bg-white p-3" data-testid="houses-summary-total">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Total houses</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{kpiTotal}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="houses-summary-active">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Active</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-emerald-700">{kpiActive}</div>
        </div>
        <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3" data-testid="houses-summary-capacity">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Total capacity</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-sky-700">{kpiCapacity}</div>
        </div>
        <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="houses-summary-rooms">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Total rooms</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-violet-700">{kpiRooms}</div>
        </div>
      </div>

      {/* Active / Archived sections */}
      {houses.length > 0 && (
        <Tabs defaultValue="active" data-testid="houses-sections">
          <TabsList>
            <TabsTrigger value="active" data-testid="houses-tab-active">
              <Home className="h-4 w-4 mr-1.5" /> Active properties
              <Badge variant="secondary" className="ml-2 tabular-nums" data-testid="houses-tab-active-count">{activeHouses.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="archived" data-testid="houses-tab-archived">
              <Archive className="h-4 w-4 mr-1.5" /> Archived properties
              <Badge variant="secondary" className="ml-2 tabular-nums" data-testid="houses-tab-archived-count">{archivedHouses.length}</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active" className="mt-4" data-testid="houses-active-section">
            {activeHouses.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{activeHouses.map(renderHouseCard)}</div>
            ) : (
              <Card><CardContent className="p-0">
                <EmptyState icon={Home} title="No active properties" description="All properties are archived. Restore one from the Archived tab or add a new house." testid="houses-active-empty" />
              </CardContent></Card>
            )}
          </TabsContent>

          <TabsContent value="archived" className="mt-4" data-testid="houses-archived-section">
            {archivedHouses.length > 0 ? (
              <>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <p className="text-sm text-muted-foreground" data-testid="houses-archived-note">Archived properties are hidden from rostering and allocations. Restore a property to make it active again.</p>
                  {isAdmin && (
                    <Button variant="destructive" size="sm" className="shrink-0" disabled={emptying}
                      onClick={() => handleEmptyArchived(archivedHouses.length)} data-testid="houses-empty-archived">
                      <Trash2 className="h-4 w-4 mr-1.5" />{emptying ? 'Deleting…' : 'Empty archived'}
                    </Button>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{archivedHouses.map(renderHouseCard)}</div>
              </>
            ) : (
              <Card><CardContent className="p-0">
                <EmptyState icon={Archive} title="No archived properties" description="Properties you archive will appear here." testid="houses-archived-empty" />
              </CardContent></Card>
            )}
          </TabsContent>
        </Tabs>
      )}

      {houses.length === 0 && (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={Home}
              title="No houses yet"
              description={isAdmin ? 'Create your first SIL house to start onboarding residents.' : 'No accommodation has been set up yet.'}
              action={isAdmin ? (
                <Button onClick={() => setShowCreateDialog(true)} data-testid="houses-empty-add-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Add House
                </Button>
              ) : null}
              testid="houses-empty"
            />
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-house-dialog" className="max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Create New House</DialogTitle>
              <DialogDescription>Add a new house/property to the system</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="house_no">House Number *</Label>
                <Input
                  id="house_no"
                  value={formData.house_no}
                  onChange={(e) => setFormData({ ...formData, house_no: e.target.value })}
                  placeholder="e.g., HOUSE-001"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Sunrise House"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Full address"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="capacity">Capacity *</Label>
                <Input
                  id="capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  placeholder="Number of residents"
                  required
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create_image">House image</Label>
                <Input id="create_image" type="file" accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                  data-testid="house-create-image-input" />
                <p className="text-xs text-muted-foreground">JPEG, PNG or WebP, up to 5 MB. Uploaded after the house is created.</p>
              </div>
              <SiteInfoFields formData={formData} setFormData={setFormData} idPrefix="create" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create House'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleEdit}>
            <DialogHeader>
              <DialogTitle>Edit House</DialogTitle>
              <DialogDescription>Update house information</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit_house_no">House Number *</Label>
                <Input
                  id="edit_house_no"
                  value={formData.house_no}
                  onChange={(e) => setFormData({ ...formData, house_no: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_name">Name *</Label>
                <Input
                  id="edit_name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_address">Address *</Label>
                <Input
                  id="edit_address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_capacity">Capacity *</Label>
                <Input
                  id="edit_capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  required
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_image">House image</Label>
                {selectedHouse?.image_url ? (
                  <HouseImage houseId={selectedHouse.id} imageUrl={selectedHouse.image_url}
                    className="h-32 w-full object-cover rounded-md border" />
                ) : null}
                <Input id="edit_image" type="file" accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                  data-testid="house-edit-image-input" />
                <p className="text-xs text-muted-foreground">Upload a new image to replace the current one (JPEG, PNG or WebP, up to 5 MB).</p>
              </div>
              <SiteInfoFields formData={formData} setFormData={setFormData} idPrefix="edit" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
